import { useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Laptop, Tablet, Watch, Shield, Clock, Wrench, Star, ArrowRight, ChevronRight, CheckCircle, Lock, ShoppingCart, Plus } from "lucide-react";
import { testimonials, trustBadges, iphoneData } from "../data/repairData";
import { useCart } from "../context/CartContext";

const deviceCards = [
  {
    icon: Smartphone,
    title: "iPhone",
    desc: "Toutes séries — 6 à 16",
    path: "/reparation/iphone",
    highlight: "Dès 39€",
    image: "https://images.pexels.com/photos/6755075/pexels-photo-6755075.jpeg?auto=compress&cs=tinysrgb&h=400",
  },
  {
    icon: Laptop,
    title: "Mac",
    desc: "MacBook Air, Pro, iMac",
    path: "/reparation/mac",
    highlight: "Dès 149€",
    image: "https://images.unsplash.com/photo-1632893037506-aac33bf18107?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    icon: Tablet,
    title: "iPad",
    desc: "iPad, Air, Pro, mini",
    path: "/reparation/ipad",
    highlight: "Dès 59€",
    image: "https://images.pexels.com/photos/1334596/pexels-photo-1334596.jpeg?auto=compress&cs=tinysrgb&h=400",
  },
  {
    icon: Watch,
    title: "Apple Watch",
    desc: "Series 1 à 9 & Ultra",
    path: "/reparation/apple-watch",
    highlight: "Dès 79€",
    image: "https://images.pexels.com/photos/3646165/pexels-photo-3646165.jpeg?auto=compress&cs=tinysrgb&h=400",
  },
];

const iconMap = { Shield, Clock, Wrench, Star };

const popularRepairs = [
  { device: "iPhone 15 Pro", repair: "Remplacement écran", price: 329, delay: "~2h" },
  { device: "MacBook Air M2", repair: "Remplacement batterie", price: 169, delay: "~1h" },
  { device: "iPhone 14", repair: "Remplacement batterie", price: 79, delay: "~30min" },
  { device: "iPad Air 4", repair: "Remplacement vitre", price: 179, delay: "~2h" },
  { device: "iPhone 13 Pro", repair: "Remplacement écran", price: 229, delay: "~2h" },
  { device: "MacBook Pro 13\" M1", repair: "Remplacement écran", price: 429, delay: "24-48h" },
];

export default function HomePage() {
  const { addItem } = useCart();
  const [efiGamme, setEfiGamme] = useState("2007-2010");
  const [efiAdded, setEfiAdded] = useState(false);
  const efiPrices = { "2007-2010": 100, "2011-2015": 140, "2016-2019": 180, "2020-2026": 280 };

  const handleEfiAdd = () => {
    addItem({
      id: `efi_deblocage_${efiGamme.replace("-", "_")}`,
      name: `Déblocage EFI & iCloud (${efiGamme}) — Envoi postal`,
      price: efiPrices[efiGamme],
      type: "repair",
    });
    setEfiAdded(true);
    setTimeout(() => setEfiAdded(false), 1500);
  };
  return (
    <div className="bg-[#0A0A0C] min-h-screen">
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1646666477664-d19273808d5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-[#0A0A0C]/90 to-[#0A0A0C]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-16">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF5C00]/10 border border-[#FF5C00]/20 mb-8">
              <div className="w-2 h-2 rounded-full bg-[#FF5C00]" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#FF5C00]">Sans rendez-vous — Lun-Sam 11h–20h</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] text-white mb-6">
              Votre Apple<br />
              entre de <span className="text-[#FF5C00]">bonnes</span><br />
              mains.
            </h1>

            <p className="text-lg text-white/60 leading-relaxed mb-8 max-w-xl">
              Réparation iPhone, Mac, iPad et Apple Watch à Paris. Techniciens spécialisés, pièces de qualité, diagnostic gratuit.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/devis"
                data-testid="hero-devis-btn"
                className="flex items-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white font-semibold px-7 py-4 rounded-full transition-all duration-200 hover:-translate-y-1 shadow-lg shadow-[#FF5C00]/25"
              >
                Devis gratuit <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-rdv-btn"
                className="flex items-center gap-2 bg-transparent text-white border border-white/20 hover:border-white/50 font-semibold px-7 py-4 rounded-full transition-colors"
              >
                Prendre RDV
              </Link>
            </div>

            {/* Quick stats */}
            <div className="flex flex-wrap gap-8 mt-12">
              {[
                { value: "5 ans+", label: "d'expérience" },
                { value: "3000+", label: "appareils réparés" },
                { value: "4.9/5", label: "satisfaction client" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-2xl font-black text-white">{stat.value}</div>
                  <div className="text-sm text-white/40">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section data-testid="trust-badges" className="border-y border-white/5 bg-[#121215]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {trustBadges.map((badge) => {
              const Icon = iconMap[badge.icon] || Shield;
              return (
                <div key={badge.title} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FF5C00]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#FF5C00]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{badge.title}</p>
                    <p className="text-xs text-white/40 leading-tight">{badge.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Déblocage EFI / iCloud ── */}
      <section data-testid="efi-section" className="py-16 bg-[#121215] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl border border-[#FF5C00]/20 bg-gradient-to-br from-[#FF5C00]/10 via-[#1A1A1E] to-[#1A1A1E] p-8 md:p-12">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#FF5C00]/5 pointer-events-none" />
            <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-[#FF5C00]/5 pointer-events-none" />

            {/* 3 pictogrammes — pleine largeur, centrés */}
            <div className="relative flex items-center justify-center gap-3 sm:gap-6 mb-10 w-full">
              {/* Picto 1 — Mac bloqué */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-[#1A1A1E] border border-[#FF5C00]/40 flex items-center justify-center shadow-lg shadow-[#FF5C00]/10">
                  <svg viewBox="0 0 32 32" className="w-8 h-8 sm:w-11 sm:h-11" fill="none">
                    <circle cx="16" cy="16" r="12" stroke="#FF5C00" strokeWidth="2.2"/>
                    <line x1="7.5" y1="7.5" x2="24.5" y2="24.5" stroke="#FF5C00" strokeWidth="2.2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="text-xs sm:text-sm text-white/50 text-center leading-tight font-medium">Mac<br/>bloqué</span>
              </div>
              <span className="text-white/25 text-base sm:text-xl flex-shrink-0" style={{marginBottom: '1.5rem'}}>→</span>
              {/* Picto 2 — Verrou EFI */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-[#FF5C00]/15 border-2 border-[#FF5C00] flex items-center justify-center shadow-lg shadow-[#FF5C00]/25">
                  <svg viewBox="0 0 32 32" className="w-8 h-8 sm:w-11 sm:h-11" fill="none">
                    <rect x="8" y="13" width="16" height="11" rx="2" fill="#FF5C00" opacity="0.15" stroke="#FF5C00" strokeWidth="2"/>
                    <path d="M11 13V10a5 5 0 0110 0v3" stroke="#FF5C00" strokeWidth="2" strokeLinecap="round"/>
                    <rect x="9" y="20" width="14" height="2.5" rx="1.25" fill="#FF5C00" opacity="0.5"/>
                    <circle cx="16" cy="17.5" r="1.5" fill="#FF5C00"/>
                  </svg>
                </div>
                <span className="text-xs sm:text-sm text-white/50 text-center leading-tight font-medium">Verrou<br/>EFI</span>
              </div>
              <span className="text-white/25 text-base sm:text-xl flex-shrink-0" style={{marginBottom: '1.5rem'}}>→</span>
              {/* Picto 3 — Dossier clignotant */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-[#1A1A1E] border border-[#FF5C00]/40 flex items-center justify-center shadow-lg shadow-[#FF5C00]/10">
                  <svg viewBox="0 0 32 32" className="w-8 h-8 sm:w-11 sm:h-11" fill="none">
                    <path d="M4 10a2 2 0 012-2h6l2 2h12a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V10z" stroke="#FF5C00" strokeWidth="2" fill="#FF5C00" fillOpacity="0.08"/>
                    <text x="16" y="22.5" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#FF5C00">?</text>
                  </svg>
                </div>
                <span className="text-xs sm:text-sm text-white/50 text-center leading-tight font-medium">Dossier<br/>clignotant</span>
              </div>
            </div>

            <div className="relative flex flex-col lg:flex-row gap-8 items-start lg:items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#FF5C00]">Service Exclusif</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-white mb-3">
                  Déblocage EFI & iCloud
                </h2>
                <p className="text-white/60 text-lg leading-relaxed max-w-xl mb-6">
                  Votre Mac affiche un cadenas ou demande un mot de passe à 4/6 chiffres ?
                  Nous débloquons <span className="text-white font-semibold">en moins d'1 heure</span> — en atelier ou par envoi postal.
                  Compatible MacBook, MacBook Air, MacBook Pro, Mac Mini et iMac (2007–2026).
                </p>
                <div className="flex flex-wrap gap-3">
                  {["Mot de passe EFI/BIOS","Verrouillage iCloud","Mot de passe de session","Envoi postal possible"].map((item) => (
                    <span key={item} className="inline-flex items-center gap-1.5 text-sm text-white/70 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">
                      <CheckCircle className="w-3.5 h-3.5 text-[#FF5C00] flex-shrink-0" />{item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-4 flex-shrink-0 w-full lg:w-auto">
                {/* Tarif card avec sélecteur */}
                <div className="p-5 rounded-2xl bg-[#0A0A0C] border border-white/10 min-w-[240px]">
                  <p className="text-xs text-white/40 uppercase tracking-widest mb-2">Choisir une option</p>

                  {/* Sélecteur de gamme — 2×2 */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {Object.entries(efiPrices).map(([gamme, prix]) => (
                      <button
                        key={gamme}
                        onClick={() => setEfiGamme(gamme)}
                        data-testid={`efi-gamme-${gamme}`}
                        className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all duration-200 ${
                          efiGamme === gamme
                            ? "bg-[#FF5C00] text-white"
                            : "bg-white/5 text-white/40 hover:text-white hover:bg-white/10"
                        }`}
                      >
                        {gamme}<br />
                        <span className={`text-base font-black ${efiGamme === gamme ? "text-white" : "text-white/60"}`}>
                          {prix}€
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-white/30">Sélectionnez la gamme de votre Mac</p>
                </div>

                {/* Bouton Ajouter au panier */}
                <button
                  data-testid="efi-add-to-cart-btn"
                  onClick={handleEfiAdd}
                  className={`flex items-center justify-center gap-2 font-semibold px-6 py-4 rounded-full transition-all duration-200 shadow-lg ${
                    efiAdded
                      ? "bg-[#34C759] text-white shadow-[#34C759]/25"
                      : "bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/40"
                  }`}
                >
                  {efiAdded
                    ? <><CheckCircle className="w-4 h-4" /> Ajouté au panier !</>
                    : <><ShoppingCart className="w-4 h-4" /> Ajouter au panier — {efiPrices[efiGamme]}€</>
                  }
                </button>

                {/* Bouton Débloquer mon Mac (contact) */}
                <Link to="/contact" data-testid="efi-contact-btn" className="flex items-center justify-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white font-semibold px-6 py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-[#FF5C00]/25">
                  <Lock className="w-4 h-4" />Débloquer mon Mac
                </Link>
                <a href="tel:+33611367002" data-testid="efi-call-btn" className="flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors">
                  Appeler : 06 11 36 70 02
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Device selector — Bento Grid */}
      <section data-testid="device-selector" className="py-24 max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-[#FF5C00] mb-3">Nos réparations</p>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-white">
            Quel appareil<br />devons-nous réparer ?
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {deviceCards.map((card) => (
            <Link
              key={card.path}
              to={card.path}
              data-testid={`device-card-${card.title.toLowerCase().replace(/\s/g, '-')}`}
              className="group relative overflow-hidden rounded-2xl border border-white/5 hover:border-[#FF5C00]/30 transition-all duration-300 hover-scale"
              style={{ background: "#121215" }}
            >
              {/* Image */}
              <div className="h-48 overflow-hidden">
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121215] via-[#121215]/60 to-transparent" />
              </div>

              {/* Content */}
              <div className="relative p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-xl bg-[#FF5C00]/10 flex items-center justify-center">
                    <card.icon className="w-5 h-5 text-[#FF5C00]" />
                  </div>
                  <span className="text-xs font-bold text-[#FF5C00] bg-[#FF5C00]/10 px-2 py-1 rounded-full">{card.highlight}</span>
                </div>
                <h3 className="text-xl font-black text-white">{card.title}</h3>
                <p className="text-sm text-white/50 mt-1">{card.desc}</p>
                <div className="flex items-center gap-1 mt-3 text-xs text-white/40 group-hover:text-[#FF5C00] transition-colors">
                  Voir les tarifs <ChevronRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Popular repairs pricing teaser */}
      <section data-testid="popular-repairs" className="py-24 bg-[#121215]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#FF5C00] mb-3">Réparations populaires</p>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-white">
                Tarifs transparents,<br />sans surprise.
              </h2>
            </div>
            <Link
              to="/devis"
              className="flex items-center gap-2 text-sm font-semibold text-[#FF5C00] hover:gap-3 transition-all"
              data-testid="view-all-prices-link"
            >
              Obtenir un devis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="border border-white/5 rounded-2xl overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="py-4 px-6 text-xs font-bold uppercase tracking-widest text-white/30">Appareil</th>
                  <th className="py-4 px-6 text-xs font-bold uppercase tracking-widest text-white/30">Réparation</th>
                  <th className="py-4 px-6 text-xs font-bold uppercase tracking-widest text-white/30 text-right">Prix</th>
                  <th className="py-4 px-6 text-xs font-bold uppercase tracking-widest text-white/30 hidden sm:table-cell">Délai</th>
                </tr>
              </thead>
              <tbody>
                {popularRepairs.map((r, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="py-4 px-6 text-white font-semibold">{r.device}</td>
                    <td className="py-4 px-6 text-white/60 text-sm">{r.repair}</td>
                    <td className="py-4 px-6 text-right">
                      <span className="font-black text-white">{r.price}€</span>
                    </td>
                    <td className="py-4 px-6 hidden sm:table-cell">
                      <span className="text-xs font-medium text-[#34C759] bg-[#34C759]/10 px-2 py-1 rounded-full">{r.delay}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-xs text-white/30 text-center">
            * Les tarifs affichés sont indicatifs. Un diagnostic gratuit est réalisé avant toute intervention.
          </p>
        </div>
      </section>

      {/* Why choose us */}
      <section data-testid="why-us" className="py-24 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#FF5C00] mb-3">Pourquoi nous choisir ?</p>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-white mb-6">
              L'expertise Apple,<br />l'approche artisanale.
            </h2>
            <p className="text-white/60 leading-relaxed mb-8">
              Depuis plus de 5 ans, nos techniciens spécialisés réparent vos appareils Apple avec soin et précision. Nous combinons l'expertise technique et la transparence pour vous offrir un service de confiance.
            </p>
            <ul className="space-y-4">
              {[
                "Diagnostic gratuit avant toute intervention",
                "Pièces de qualité supérieure",
                "Réparations garanties",
                "Sans rendez-vous, intervention rapide",
                "Devis transparent et sans surprise",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#FF5C00] flex-shrink-0" />
                  <span className="text-white/80 text-sm">{item}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/contact"
              data-testid="why-us-cta"
              className="inline-flex items-center gap-2 mt-8 bg-[#FF5C00] hover:bg-[#E05200] text-white font-semibold px-7 py-4 rounded-full transition-all duration-200 hover:-translate-y-1"
            >
              Nous contacter <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="relative">
            <img
              src="https://images.pexels.com/photos/6755056/pexels-photo-6755056.jpeg?auto=compress&cs=tinysrgb&h=700"
              alt="Technicien en atelier"
              className="rounded-2xl w-full h-[480px] object-cover"
            />
            <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-5 shadow-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FF5C00] flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Diagnostic gratuit</p>
                  <p className="text-xs text-white/50">Évaluation sans engagement</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials marquee */}
      <section data-testid="testimonials" className="py-20 bg-[#121215] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-[#FF5C00] mb-3 text-center">Avis clients</p>
          <h2 className="text-4xl font-black tracking-tight text-white text-center">Ce que disent nos clients</h2>
        </div>

        <div className="relative overflow-hidden">
          <div className="marquee-track flex gap-6 w-max">
            {[...testimonials, ...testimonials].map((t, i) => (
              <div
                key={i}
                className="w-72 flex-shrink-0 p-5 rounded-2xl border border-white/5"
                style={{ background: "#1A1A1E" }}
              >
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, si) => (
                    <Star key={si} className="w-3.5 h-3.5 fill-[#FF5C00] text-[#FF5C00]" />
                  ))}
                </div>
                <p className="text-sm text-white/70 leading-relaxed mb-3">"{t.text}"</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-white">{t.name}</p>
                  <p className="text-xs text-white/30">{t.date} · {t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section data-testid="cta-section" className="py-24 max-w-7xl mx-auto px-6">
        <div className="relative overflow-hidden rounded-3xl p-12 sm:p-16 text-center" style={{ background: "#121215" }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-[#FF5C00]/10 rounded-full blur-3xl" />
          <div className="relative">
            <p className="text-xs font-bold uppercase tracking-widest text-[#FF5C00] mb-4">Prêt à réparer votre appareil ?</p>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-white mb-4">
              Obtenez un devis<br />en 2 minutes
            </h2>
            <p className="text-white/50 mb-8 max-w-md mx-auto">
              Remplissez notre formulaire de devis ou venez directement en atelier sans rendez-vous.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/devis"
                data-testid="cta-devis-btn"
                className="flex items-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:-translate-y-1 shadow-lg shadow-[#FF5C00]/25"
              >
                Devis en ligne <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/nos-magasins"
                data-testid="cta-magasins-btn"
                className="flex items-center gap-2 bg-transparent text-white border border-white/20 hover:border-white/50 font-semibold px-8 py-4 rounded-full transition-colors"
              >
                Nos ateliers
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
